import React, { useEffect, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { customTheme } from 'theme';
import { NotFound } from 'pages/NotFound';
import { Loading } from 'pages/Loading';
import { Error } from 'pages/Error';
import { AppContextProvider } from 'context/app/app-context';
import { GlobalFilterProvider } from 'context/global-filter/global-filter-context';
import queryClient from 'lib/queryClient';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { AppConfig, fetchAppSettings } from 'hooks/useAppConfig';
import {
    AccountInfo,
    EventType,
    PublicClientApplication
} from '@azure/msal-browser';
import { msalConfig } from 'lib/msal';
import { MsalProvider, useIsAuthenticated } from '@azure/msal-react';
import { AuthProvider } from 'lib/AuthProvider';
import { BlockedBrowserPopup } from 'pages/BlockedBrowserPopup';
import Login from 'pages/Login';

const AuthenticatedApp = React.lazy(() => import('./AuthenticatedApp'));

const UnauthenticatedApp = React.lazy(() => import('./UnauthenticatedApp'));

export let msalInstance: PublicClientApplication;

export function App() {
    const [config, setConfig] = useState<AppConfig>();
    const [error, setError] = useState<any>(undefined);

    useEffect(() => {
        fetchAppSettings()
            .then(result => setConfig(result))
            .catch(error => setError(error));
    }, []);

    const msalWrapper = (children: React.ReactNode) => {
        const isLoading = config === undefined && error === undefined;
        const isError = error !== undefined;

        if (config !== undefined && !isError) {
            msalInstance = new PublicClientApplication(
                msalConfig({
                    SUPPORT_UI_CLIENT_ID: config.SUPPORT_UI_CLIENT_ID,
                    SUPPORT_UI_REDIRECT_URL: config.SUPPORT_UI_REDIRECT_URL,
                    TENANT_ID: config.TENANT_ID
                })
            );
        }

        return isLoading ? (
            <Loading />
        ) : config !== undefined && !isError ? (
            <MsalProvider instance={msalInstance}>{children}</MsalProvider>
        ) : (
            <Error />
        );
    };

    return (
        <ChakraProvider theme={customTheme} resetCSS={true}>
            {msalWrapper(
                <Router>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route
                            path="*"
                            element={
                                <AuthProvider>
                                    <QueryClientProvider client={queryClient}>
                                        <PagesProvider />
                                        <ReactQueryDevtools initialIsOpen />
                                    </QueryClientProvider>
                                </AuthProvider>
                            }
                        />
                        <Route
                            path="/404-page-not-found"
                            element={<NotFound />}
                        />
                        <Route
                            path="/blocked-browser-popup"
                            element={<BlockedBrowserPopup />}
                        />
                    </Routes>
                </Router>
            )}
        </ChakraProvider>
    );
}

function PagesProvider() {
    const isAuthenticated = useIsAuthenticated();

    if (
        msalInstance &&
        isAuthenticated &&
        !msalInstance.getActiveAccount() &&
        msalInstance.getAllAccounts().length > 0
    ) {
        msalInstance.enableAccountStorageEvents();
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
        msalInstance.addEventCallback(event => {
            if (
                event &&
                event.eventType === EventType.LOGIN_SUCCESS &&
                event.payload
            ) {
                const account = event.payload as AccountInfo;
                msalInstance.setActiveAccount(account);
            }
        });
    }

    return isAuthenticated ? (
        <AppContextProvider>
            <GlobalFilterProvider>
                <AuthenticatedApp />
            </GlobalFilterProvider>
        </AppContextProvider>
    ) : (
        <UnauthenticatedApp />
    );
}
